import React, { useMemo } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    useTheme,
    useMediaQuery,
    Typography,
    CircularProgress,
    Alert,
    Grid,
    Card,
    CardContent,
    IconButton,
    Tooltip,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DateRangeIcon from '@mui/icons-material/DateRange';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { motion } from 'framer-motion';
import { format, subDays, startOfMonth, endOfMonth, parseISO } from 'date-fns';
import { tr } from 'date-fns/locale';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
    ResponsiveContainer,
    LineChart,
    Line,
    PieChart,
    Pie,
    Cell
} from 'recharts';
import { getOdemelerRaporu } from '../../../services/api/odemelerService';
import { OdemelerError } from '../../../types/ErrorModals/OdemelerError';

// API veri tipi tanımlaması
interface OdemeDetay {
    tarih: string;
    tahsil: number;
    sezon: number;
    adiSoyadi: string;
}

interface OdemelerRaporuData {
    odemeler: OdemeDetay[];
    toplamTahsilat: number;
    bugunTahsilat: number;
    buHaftaTahsilat: number;
    buAyTahsilat: number;
}

// Özet kart props tipi
interface SummaryCardProps {
    title: string;
    value: number;
    subtitle?: string;
    color: string;
    icon?: React.ReactNode;
}

// Zaman aralığı tipi
type TimeRange = 'bugun' | 'buHafta' | 'buAy' | 'tumZamanlar';

const OdemelerRaporu: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [timeRange, setTimeRange] = React.useState<TimeRange>('buAy');

    const { data, isLoading, error, refetch, isRefetching } = useQuery<OdemelerRaporuData, Error>({
        queryKey: ['odemelerRaporu'],
        queryFn: async () => {
            try {
                return await getOdemelerRaporu({
                    kurumKodu: localStorage.getItem('kurumKodu') || ''
                });
            } catch (err) {
                const odemelerError = err as OdemelerError;
                throw new Error(odemelerError.message);
            }
        },
        refetchInterval: 1000 * 60 * 30, // 30 dakikada bir yenile
        enabled: !!localStorage.getItem('kurumKodu'),
        retry: 1, 
    });

    // Filtrelenmiş verileri hesapla
    const filteredData = useMemo(() => {
        if (!data?.odemeler) return [];

        const today = new Date();

        switch (timeRange) {
            case 'bugun':
                return data.odemeler.filter(odeme => {
                    const odemeDate = parseISO(odeme.tarih);
                    return format(odemeDate, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd');
                });
            case 'buHafta':
                return data.odemeler.filter(odeme => {
                    const odemeDate = parseISO(odeme.tarih);
                    const oneWeekAgo = subDays(today, 7);
                    return odemeDate >= oneWeekAgo && odemeDate <= today;
                });
            case 'buAy':
                return data.odemeler.filter(odeme => {
                    const odemeDate = parseISO(odeme.tarih);
                    const firstDayOfMonth = startOfMonth(today);
                    const lastDayOfMonth = endOfMonth(today);
                    return odemeDate >= firstDayOfMonth && odemeDate <= lastDayOfMonth;
                });
            default:
                return data.odemeler;
        }
    }, [data, timeRange]);

    // Grafik verileri hazırla
    const chartData = useMemo(() => {
        if (!filteredData.length) return [];

        // Tarih bazında verileri grupla
        const groupedByDate: Record<string, number> = {};

        filteredData.forEach(odeme => {
            const dateStr = format(parseISO(odeme.tarih), 'dd MMM', { locale: tr });
            if (!groupedByDate[dateStr]) {
                groupedByDate[dateStr] = 0;
            }
            groupedByDate[dateStr] += odeme.tahsil;
        });

        // Grafik için formatlı veri oluştur
        return Object.entries(groupedByDate).map(([date, value]) => ({
            tarih: date,
            tutar: value
        }));
    }, [filteredData]);

    // Pasta grafik verileri
    const pieData = useMemo(() => {
        if (!data) return [];

        return [
            { name: 'Bugün', value: data.bugunTahsilat },
            { name: 'Bu Hafta (Bugün hariç)', value: data.buHaftaTahsilat - data.bugunTahsilat },
            { name: 'Bu Ay (Bu Hafta hariç)', value: data.buAyTahsilat - data.buHaftaTahsilat },
            { name: 'Diğer Zamanlar', value: data.toplamTahsilat - data.buAyTahsilat }
        ].filter(item => item.value > 0);
    }, [data]);

    // Pasta grafik renkleri
    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

    // Para formatı
    const formatMoney = (amount: number) => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY',
            minimumFractionDigits: 2
        }).format(amount);
    };

    // Özet kart bileşeni
    const SummaryCard: React.FC<SummaryCardProps> = ({ title, value, subtitle, color, icon }) => (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
        >
            <Card
                elevation={0}
                sx={{
                    height: '100%',
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '1px solid rgba(0, 0, 0, 0.05)',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                        boxShadow: '0 5px 15px rgba(0, 0, 0, 0.08)',
                        transform: 'translateY(-3px)'
                    }
                }}
            >
                <CardContent sx={{ p: 3 }}>
                    <Box display="flex" alignItems="center" mb={1}>
                        <Box
                            sx={{
                                backgroundColor: `${color}`,
                                borderRadius: '50%',
                                width: 40,
                                height: 40,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mr: 2
                            }}
                        >
                            {icon || <AttachMoneyIcon sx={{ color: 'white' }} />}
                        </Box>
                        <Typography variant="subtitle1" color="text.secondary">
                            {title}
                        </Typography>
                    </Box>
                    <Typography variant="h4" component="div" fontWeight="bold" mb={1}>
                        {formatMoney(value)}
                    </Typography>
                    {subtitle && (
                        <Typography variant="body2" color="text.secondary">
                            {subtitle}
                        </Typography>
                    )}
                </CardContent>
            </Card>
        </motion.div>
    );

    // Zaman aralığı değişimi
    const handleTimeRangeChange = (event: SelectChangeEvent) => {
        setTimeRange(event.target.value as TimeRange);
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert
                severity="error"
                sx={{ mt: 2, borderRadius: 2 }}
                action={
                    <>
                        {process.env.NODE_ENV === 'development' && (
                            <Typography variant="caption" display="block">
                                CorrelationId: {(error as OdemelerError).correlationId}
                            </Typography>
                        )}
                        <IconButton color="inherit" size="small" onClick={() => refetch()}>
                            <RefreshIcon />
                        </IconButton>
                    </>
                }
            >
                {(error as Error).message}
            </Alert>
        );
    }

    if (!data) {
        return (
            <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                Ödeme verileri bulunamadı.
            </Alert>
        );
    }

    return (
        <Box sx={{ p: 3, mt: 4 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography
                    variant="h5"
                    component="h2"
                    sx={{
                        fontWeight: 'bold',
                        color: theme.palette.secondary.main
                    }}
                >
                    Ödemeler Raporu
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel id="time-range-label">Zaman Aralığı</InputLabel>
                        <Select
                            labelId="time-range-label"
                            value={timeRange}
                            label="Zaman Aralığı"
                            onChange={handleTimeRangeChange}
                        >
                            <MenuItem value="bugun">Bugün</MenuItem>
                            <MenuItem value="buHafta">Bu Hafta</MenuItem>
                            <MenuItem value="buAy">Bu Ay</MenuItem>
                            <MenuItem value="tumZamanlar">Tüm Zamanlar</MenuItem>
                        </Select>
                    </FormControl>
                    <Tooltip title="Yenile">
                        <IconButton
                            onClick={() => refetch()}
                            color="secondary"
                            disabled={isRefetching}
                            sx={{
                                animation: isRefetching ? 'spin 1s linear infinite' : 'none',
                                '@keyframes spin': {
                                    '0%': { transform: 'rotate(0deg)' },
                                    '100%': { transform: 'rotate(360deg)' }
                                }
                            }}
                        >
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* Özet Kartları */}
            <Grid container spacing={3} mb={4}>
                <Grid item xs={12} sm={6} lg={3}>
                    <SummaryCard
                        title="Toplam Tahsilat"
                        value={data.toplamTahsilat}
                        subtitle="Tüm zamanlar"
                        color="#6200EA"
                        icon={<AttachMoneyIcon sx={{ color: 'white' }} />}
                    />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                    <SummaryCard
                        title="Bu Ay Tahsilat"
                        value={data.buAyTahsilat}
                        subtitle={`${format(new Date(), 'MMMM yyyy', { locale: tr })}`}
                        color="#FF9800"
                        icon={<DateRangeIcon sx={{ color: 'white' }} />}
                    />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                    <SummaryCard
                        title="Bu Hafta Tahsilat"
                        value={data.buHaftaTahsilat}
                        subtitle="Son 7 gün"
                        color="#2196F3"
                        icon={<TrendingUpIcon sx={{ color: 'white' }} />}
                    />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                    <SummaryCard
                        title="Bugün Tahsilat"
                        value={data.bugunTahsilat}
                        subtitle={format(new Date(), 'dd MMMM yyyy', { locale: tr })}
                        color="#4CAF50"
                        icon={<ReceiptIcon sx={{ color: 'white' }} />}
                    />
                </Grid>
            </Grid>

            {/* Grafikler */}
            <Grid container spacing={3}>
                {/* Çubuk Grafik */}
                <Grid item xs={12} lg={8}>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.2 }}
                    >
                        <Card
                            elevation={0}
                            sx={{
                                borderRadius: 2,
                                overflow: 'hidden',
                                border: '1px solid rgba(0, 0, 0, 0.05)',
                                height: '100%',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    boxShadow: '0 5px 15px rgba(0, 0, 0, 0.08)'
                                }
                            }}
                        >
                            <CardContent>
                                <Typography variant="h6" gutterBottom fontWeight="bold">
                                    {timeRange === 'bugun' ? 'Bugünkü' :
                                        timeRange === 'buHafta' ? 'Bu Haftaki' :
                                            timeRange === 'buAy' ? 'Bu Ayki' : 'Tüm'} Ödemeler
                                </Typography>
                                <Divider sx={{ mb: 2 }} />

                                {chartData.length > 0 ? (
                                    <Box sx={{ height: 300, mt: 2 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart
                                                data={chartData}
                                                margin={{
                                                    top: 5,
                                                    right: 30,
                                                    left: 20,
                                                    bottom: 5,
                                                }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="tarih" />
                                                <YAxis
                                                    tickFormatter={(value) =>
                                                        new Intl.NumberFormat('tr-TR', {
                                                            style: 'currency',
                                                            currency: 'TRY',
                                                            minimumFractionDigits: 0,
                                                            maximumFractionDigits: 0,
                                                        }).format(value)
                                                    }
                                                />
                                                <RechartsTooltip
                                                    formatter={(value: number) => [
                                                        formatMoney(value),
                                                        "Tahsilat"
                                                    ]}
                                                />
                                                <Legend />
                                                <Bar
                                                    dataKey="tutar"
                                                    name="Tahsilat"
                                                    fill="#8884d8"
                                                    radius={[4, 4, 0, 0]}
                                                />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </Box>
                                ) : (
                                    <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                                        <Typography variant="body1" color="text.secondary">
                                            Seçilen zaman aralığında veri bulunmamaktadır.
                                        </Typography>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </motion.div>
                </Grid>

                {/* Pasta Grafik */}
                <Grid item xs={12} lg={4}>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.3 }}
                    >
                        <Card
                            elevation={0}
                            sx={{
                                borderRadius: 2,
                                overflow: 'hidden',
                                border: '1px solid rgba(0, 0, 0, 0.05)',
                                height: '100%',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    boxShadow: '0 5px 15px rgba(0, 0, 0, 0.08)'
                                }
                            }}
                        >
                            <CardContent>
                                <Typography variant="h6" gutterBottom fontWeight="bold">
                                    Tahsilat Dağılımı
                                </Typography>
                                <Divider sx={{ mb: 2 }} />

                                {pieData.length > 0 ? (
                                    <Box sx={{ height: 300, mt: 2 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={pieData}
                                                    cx="50%"
                                                    cy="50%"
                                                    labelLine={false}
                                                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                                    outerRadius={80}
                                                    fill="#8884d8"
                                                    dataKey="value"
                                                >
                                                    {pieData.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                    ))}
                                                </Pie>
                                                <RechartsTooltip
                                                    formatter={(value: number) => [
                                                        formatMoney(value),
                                                        "Tahsilat"
                                                    ]}
                                                />
                                                <Legend />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </Box>
                                ) : (
                                    <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                                        <Typography variant="body1" color="text.secondary">
                                            Tahsilat verisi bulunmamaktadır.
                                        </Typography>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </motion.div>
                </Grid>

                {/* Tablo */}
                <Grid item xs={12}>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.4 }}
                    >
                        <Card
                            elevation={0}
                            sx={{
                                borderRadius: 2,
                                overflow: 'hidden',
                                border: '1px solid rgba(0, 0, 0, 0.05)',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    boxShadow: '0 5px 15px rgba(0, 0, 0, 0.08)'
                                }
                            }}
                        >
                            <CardContent>
                                <Typography variant="h6" gutterBottom fontWeight="bold">
                                    Ödeme Detayları
                                </Typography>
                                <Divider sx={{ mb: 2 }} />

                                {filteredData.length > 0 ? (
                                    <TableContainer sx={{ maxHeight: 400 }}>
                                        <Table stickyHeader size={isMobile ? "small" : "medium"}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Tarih</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Tahsilat Tutarı</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Adı Soyadı</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Sezon</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {filteredData.map((odeme, index) => (
                                                    <TableRow
                                                        key={index}
                                                        sx={{
                                                            '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' },
                                                            '&:hover': {
                                                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                                transition: 'background-color 0.3s ease',
                                                            },
                                                        }}
                                                    >
                                                        <TableCell>
                                                            {format(parseISO(odeme.tarih), 'dd MMMM yyyy', { locale: tr })}
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 'medium' }}>
                                                            {formatMoney(odeme.tahsil)}
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 'medium' }}>
                                                            {odeme.adiSoyadi}
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 'medium' }}>
                                                            {odeme.sezon}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Toplam</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}></TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}></TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                                        {formatMoney(
                                                            filteredData.reduce((sum, item) => sum + item.tahsil, 0)
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                ) : (
                                    <Box display="flex" justifyContent="center" alignItems="center" height={100}>
                                        <Typography variant="body1" color="text.secondary">
                                            Seçilen zaman aralığında ödeme verisi bulunmamaktadır.
                                        </Typography>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </motion.div>
                </Grid>

                {/* Çizgi Grafik - Trend Analizi */}
                <Grid item xs={12}>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.5 }}
                    >
                        <Card
                            elevation={0}
                            sx={{
                                borderRadius: 2,
                                overflow: 'hidden',
                                border: '1px solid rgba(0, 0, 0, 0.05)',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    boxShadow: '0 5px 15px rgba(0, 0, 0, 0.08)'
                                }
                            }}
                        >
                            <CardContent>
                                <Typography variant="h6" gutterBottom fontWeight="bold">
                                    Ödeme Trendi
                                </Typography>
                                <Divider sx={{ mb: 2 }} />

                                {chartData.length > 0 ? (
                                    <Box sx={{ height: 300, mt: 2 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <LineChart
                                                data={chartData}
                                                margin={{
                                                    top: 5,
                                                    right: 30,
                                                    left: 20,
                                                    bottom: 5,
                                                }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="tarih" />
                                                <YAxis
                                                    tickFormatter={(value) =>
                                                        new Intl.NumberFormat('tr-TR', {
                                                            style: 'currency',
                                                            currency: 'TRY',
                                                            minimumFractionDigits: 0,
                                                            maximumFractionDigits: 0,
                                                        }).format(value)
                                                    }
                                                />
                                                <RechartsTooltip
                                                    formatter={(value: number) => [
                                                        formatMoney(value),
                                                        "Tahsilat"
                                                    ]}
                                                />
                                                <Legend />
                                                <Line
                                                    type="monotone"
                                                    dataKey="tutar"
                                                    name="Tahsilat"
                                                    stroke="#8884d8"
                                                    activeDot={{ r: 8 }}
                                                    strokeWidth={2}
                                                />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </Box>
                                ) : (
                                    <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                                        <Typography variant="body1" color="text.secondary">
                                            Seçilen zaman aralığında veri bulunmamaktadır.
                                        </Typography>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </motion.div>
                </Grid>
            </Grid>
        </Box>
    );
};

export default OdemelerRaporu;